/**
 * Contact list view — the main People tab content.
 * Supports feed view (default) and list view toggle.
 * Receives contacts from the useContacts hook (Apple Contacts on iOS).
 */
import { useMemo, useState } from "react";
import { ChevronRight, LayoutGrid, List, Heart } from "lucide-react";
import { Avatar, AvatarFallback } from "@/ui/Avatar";
import { getInitials, getFullName, getRoleDisplay, type Contact } from "@/lib/data";
import { PeopleFeed } from "@/people/PeopleFeed";

interface ContactListProps {
  contacts: Contact[];
  onSelectContact: (contact: Contact) => void;
  onUpdateContact: (contactId: string, updates: Partial<Contact>) => void;
}

export function ContactList({ contacts, onSelectContact, onUpdateContact }: ContactListProps) {
  const [viewMode, setViewMode] = useState<"feed" | "list">("feed");

  const { attention, all } = useMemo(() => {
    const sorted = [...contacts].sort((a, b) => a.firstName.localeCompare(b.firstName));
    return {
      attention: sorted.filter((c) => c.status === "attention"),
      all: sorted.filter((c) => c.status !== "attention"),
    };
  }, [contacts]);

  const handleToggleFavorite = (contactId: string) => {
    const contact = contacts.find((c) => c.id === contactId);
    if (contact) {
      onUpdateContact(contactId, { favorite: !contact.favorite });
    }
  };

  return (
    <div className="flex flex-col pb-0">
      {/* Header */}
      <div className="px-5 pb-3 pt-[calc(env(safe-area-inset-top,0px)+16px)]">
        <div className="flex items-center justify-between">
          <h1 className="text-foreground text-[34px] font-bold tracking-tight">People</h1>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode((v) => (v === "feed" ? "list" : "feed"))}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-[rgba(255,255,255,0.06)] active:scale-95"
            >
              {viewMode === "feed" ? (
                <List size={14} className="text-[rgba(255,255,255,0.5)]" />
              ) : (
                <LayoutGrid size={14} className="text-[rgba(255,255,255,0.5)]" />
              )}
            </button>
          </div>
        </div>
        <p className="text-muted-foreground mt-1 text-[13px]">{contacts.length} contacts</p>
      </div>

      {/* Feed view (default) */}
      {viewMode === "feed" && (
        <PeopleFeed
          contacts={contacts}
          onSelectContact={onSelectContact}
          onToggleFavorite={handleToggleFavorite}
        />
      )}

      {/* List view */}
      {viewMode === "list" && (
        <>
          {attention.length > 0 && (
            <div className="px-5 pb-1 pt-1">
              <p className="text-[#FB923C] mb-2 text-[12px] font-semibold uppercase tracking-[0.08em]">
                Need you
              </p>
              {attention.map((contact) => (
                <ContactRow
                  key={contact.id}
                  contact={contact}
                  onSelect={onSelectContact}
                  onToggleFavorite={handleToggleFavorite}
                  highlight
                />
              ))}
            </div>
          )}

          {all.length > 0 && (
            <div className="px-5 pb-28 pt-3">
              {all.map((contact, i) => (
                <ContactRow
                  key={contact.id}
                  contact={contact}
                  onSelect={onSelectContact}
                  onToggleFavorite={handleToggleFavorite}
                  showDivider={i < all.length - 1}
                />
              ))}
            </div>
          )}

          {contacts.length === 0 && (
            <div className="px-5 py-20 text-center">
              <p className="text-[16px] text-[rgba(255,255,255,0.4)]">No contacts found</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ContactRow({
  contact,
  onSelect,
  onToggleFavorite,
  highlight,
  showDivider,
}: {
  contact: Contact;
  onSelect: (c: Contact) => void;
  onToggleFavorite: (id: string) => void;
  highlight?: boolean;
  showDivider?: boolean;
}) {
  return (
    <div
      className={`flex w-full items-center gap-3 py-3 ${
        highlight ? "bg-[rgba(251,146,60,0.08)] mb-1.5 rounded-xl px-3" : ""
      }`}
    >
      <button
        onClick={(e) => {
          e.stopPropagation();
          onToggleFavorite(contact.id);
        }}
        className="shrink-0 p-0.5 active:scale-90"
      >
        <Heart
          size={14}
          className={
            contact.favorite
              ? "fill-primary text-primary"
              : "text-[rgba(255,255,255,0.15)]"
          }
        />
      </button>

      <button
        onClick={() => onSelect(contact)}
        className="flex min-w-0 flex-1 items-center gap-3 text-left active:opacity-70"
      >
        <Avatar className="h-10 w-10 shrink-0">
          <AvatarFallback status={contact.status} className="text-[14px]">
            {getInitials(contact)}
          </AvatarFallback>
        </Avatar>

        <div
          className={`flex min-w-0 flex-1 items-center gap-2 ${
            showDivider && !highlight ? "border-border border-b pb-3" : ""
          }`}
        >
          <div className="min-w-0 flex-1">
            <span className="text-foreground text-[16px] font-normal">{getFullName(contact)}</span>
            <p className="text-muted-foreground truncate text-[13px]">{getRoleDisplay(contact)}</p>
          </div>

          {contact.status === "attention" && (
            <div className="bg-[#FB923C] h-2 w-2 shrink-0 rounded-full" />
          )}

          <ChevronRight size={16} className="shrink-0 text-[rgba(255,255,255,0.2)]" />
        </div>
      </button>
    </div>
  );
}
